// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Catch Pass  (API + schedule helpers for pages/CatchPass.tsx)
// A Catch Pass plan is a regular box the customer saved: catches (pack size + quantity + cut), a rhythm (weekly / every 2 weeks /
// monthly) and a preferred service day. GET/POST/PUT/DELETE /api/me/plans → backend `customers/{uid}/plans`. It is NOT auto-billed:
// nothing is charged and no order is created for the customer — "Add box to cart" fills the cart and they pay at checkout like any
// order. "Due" is an in-app reminder computed from the last time the box was added to the cart (or when the plan was created).
// ─────────────────────────────────────────────────────────────────────────────
import type { CutId } from "../data/cuts";
import type { PackId } from "../data/packs";
import { apiAuthed } from "./api";

export type Cadence = "weekly" | "fortnightly" | "monthly";
export type Day = "wednesday" | "saturday" | "sunday";
export type PlanStatus = "active" | "paused";

export interface PlanLine {
  catchId: string;
  pack: PackId;
  qty: number;
  cut: CutId | null;
}

export interface Plan {
  id: string;
  name: string;
  cadence: Cadence;
  day: Day;
  lines: PlanLine[];
  status: PlanStatus;
  createdAt: string;
  lastAddedAt: string | null;
}

export interface PlanInput {
  name: string;
  cadence: Cadence;
  day: Day;
  lines: PlanLine[];
}

export const MAX_PLANS = 5;

export const CADENCES: { id: Cadence; label: string; days: number }[] = [
  { id: "weekly", label: "Every week", days: 7 },
  { id: "fortnightly", label: "Every 2 weeks", days: 14 },
  { id: "monthly", label: "Every month", days: 30 },
];

/** The service days in the published Terms (§9.3); `js` is Date#getDay(). */
export const DAYS: { id: Day; label: string; js: number }[] = [
  { id: "wednesday", label: "Wednesday", js: 3 },
  { id: "saturday", label: "Saturday", js: 6 },
  { id: "sunday", label: "Sunday", js: 0 },
];

export const cadenceLabel = (cadence: Cadence) => CADENCES.find((c) => c.id === cadence)?.label ?? cadence;
export const dayLabel = (day: Day) => DAYS.find((d) => d.id === day)?.label ?? day;

/** When the next box is due: the cadence after the last add (or creation), moved forward to the plan's service day. */
export function nextDue(plan: Plan): Date {
  const base = new Date(plan.lastAddedAt ?? plan.createdAt);
  const cadence = CADENCES.find((c) => c.id === plan.cadence);
  const due = new Date(base.getTime() + (cadence?.days ?? 7) * 86_400_000);
  const wanted = DAYS.find((d) => d.id === plan.day)?.js ?? 6;
  while (due.getDay() !== wanted) due.setDate(due.getDate() + 1);
  return due;
}

/** An active plan is due once its next-due day has come (paused plans never nag). */
export const isDue = (plan: Plan, now = new Date()) => plan.status === "active" && nextDue(plan).setHours(0, 0, 0, 0) <= now.setHours(0, 0, 0, 0);

export const fetchPlans = () => apiAuthed<Plan[]>("GET", "/me/plans");
export const createPlan = (input: PlanInput) => apiAuthed<Plan>("POST", "/me/plans", input);
export const updatePlan = (id: string, input: PlanInput) => apiAuthed<Plan>("PUT", `/me/plans/${id}`, input);
export const setPlanPaused = (id: string, paused: boolean) => apiAuthed<Plan>("POST", `/me/plans/${id}/${paused ? "pause" : "resume"}`);
export const markPlanAdded = (id: string) => apiAuthed<Plan>("POST", `/me/plans/${id}/added`);
export const deletePlan = (id: string) => apiAuthed<{ deleted: boolean }>("DELETE", `/me/plans/${id}`);
